import { IntlShape, MessageDescriptor } from "react-intl";
import { Map } from "immutable";

import type { ValidationRules } from "./models";
import { ValidateField, ValueLabelPair } from "./models";
import { ValidationRules as validation } from "./rules";
import { messages } from "./messages";

type FormatMessageFunc = (
    messageDescriptor: MessageDescriptor,
    values?: {[key: string]: string | number | boolean | Date},
) => string;

function instanceOfMessage(object: any): object is MessageDescriptor {
    return (object as MessageDescriptor).id !== undefined;
}

function formatError(
    formatMessage: FormatMessageFunc,
    message: MessageDescriptor,
    messageProps: any,
): string {
    if (formatMessage != null) {
        return formatMessage(message, messageProps);
    }
    else {
        return typeof message.defaultMessage === "string" ? message.defaultMessage : message.id;
    }
}

export function testRulesOnValue(
    value: string,
    label: string,
    rules: ValidateField[],
    formatMessage: FormatMessageFunc,
): string {
    let error = null;

    for (const rule of rules) {
        let message: MessageDescriptor = null;
        const messageProps: any = {
            column: label,
        };
        if (rule.type === "required" && !validation.required(value)) {
            message = messages.requiredMessages;
        }
        else if (rule.type === "minLength" && !validation.minLength(value, rule.length)) {
            message = messages.minLengthMessages;
            messageProps.min = rule.length;
        }
        else if (rule.type === "maxLength" && !validation.maxLength(value, rule.length)) {
            message = messages.maxLengthMessages;
            messageProps.max = rule.length;
        }
        else if (rule.type === "email" && !validation.email(value)) {
            message = messages.emailMessages;
        }
        else if (rule.type === "min" && !validation.min(Number(value), rule.size)) {
            message = messages.minMessages;
            messageProps.min = rule.size;
        }
        else if (rule.type === "max" && !validation.max(Number(value), rule.size)) {
            message = messages.maxMessages;
            messageProps.max = rule.size;
        }
        else if (rule.type === "pattern" && !validation.pattern(value, rule.pattern)) {
            message = messages.patternMessages;
            messageProps.pattern = rule.pattern;
        }
        else if (rule.type === "equalTo" && !validation.equalTo(value, rule.property.value)) {
            message = messages.equalToMessages;
            messageProps.match = rule.property.label;
        }
        else if (rule.type === "oneOf" &&
            !validation.oneOf(value, rule.properties.map(
                (prop: ValueLabelPair) => prop.value,
            ))
        ) {
            message = messages.oneOfMessages;
            messageProps.matches = rule.properties.map(
                (prop: ValueLabelPair) => prop.label,
            ).join(", ");
        }
        else if (rule.type === "url" && !validation.url(value)) {
            message = messages.urlMessages;
        }

        if (message !== null) {
            error = formatError(formatMessage, message, messageProps);
            break;
        }
    }

    return error;
}

function validate(rules: ValidationRules) {
    return (values: any, props: { intl: IntlShape }) => {
        let errors = Map<string, string>();

        // If we have access to intl, we can translate messages.
        let formatMessage: FormatMessageFunc = null;
        if (props.intl != null) {
            formatMessage = props.intl.formatMessage;
        }

        // Loop through the rules and build up the errors.
        const rulesMap = Map(rules);
        rulesMap.map((rule, col) => {
            // Get the label.
            let label = col;
            if (rule.label != null) {
                const colLabel = rule.label;
                if (formatMessage != null && instanceOfMessage(colLabel)) {
                    label = formatMessage(colLabel);
                }
                else if (typeof colLabel === "string") {
                    label = colLabel;
                }
            }
            label = label.toLowerCase();

            // Get the actual value to validate.
            let error = null;
            const value = values[col];
            if (Array.isArray(value)) {
                for (const val of value) {
                    if (typeof val === "object" && val.value !== undefined) {
                        error = testRulesOnValue(val.value, label, rule.rules, formatMessage);
                    }
                    else {
                        error = testRulesOnValue(val, label, rule.rules, formatMessage);
                    }

                    // If we've already matched, we don't need to go again.
                    if (error != null) {
                        break;
                    }
                }
            }
            else if (typeof value === "object" && value != null && value.value !== undefined) {
                error = testRulesOnValue(value.value, label, rule.rules, formatMessage);
            }
            else {
                error = testRulesOnValue(value, label, rule.rules, formatMessage);
            }

            if (error !== null) {
                errors = errors.set(col, error);
            }
        });

        return errors.toJS();
    };
}

export {
    ValidationRules,
    validate,
};
